import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    LayoutDashboard,
    Settings,
    Image as ImageIcon,
    Info,
    Briefcase,
    MessageSquare,
    Plus,
    Edit,
    Trash2,
    Save,
    LogOut,
    Upload,
    ChevronRight,
    CheckCircle2,
    X
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Admin = () => {
    const { data, refreshData, loading: dataLoading, error: dataError } = useData();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const [activeTab, setActiveTab] = useState('settings');
    const [loading, setLoading] = useState(false);

    // Form states
    const [settingsForm, setSettingsForm] = useState({});
    const [aboutForm, setAboutForm] = useState({});
    const [editingItem, setEditingItem] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    const handlePasswordChange = async () => {
        if (!newPassword) {
            toast.error('Lütfen yeni şifreyi girin');
            return;
        }
        setLoading(true);
        try {
            const response = await fetch('/api/change_password.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ new_password: newPassword })
            });
            const result = await response.json();
            if (result.success) {
                toast.success('Şifre başarıyla güncellendi');
                setNewPassword('');
            } else {
                toast.error('Hata: ' + result.message);
            }
        } catch (error) {
            toast.error('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean']
        ],
    };

    useEffect(() => {
        const adminToken = localStorage.getItem('adminToken');
        if (adminToken) setIsLoggedIn(true);

        if (data) {
            setSettingsForm(data.settings || {});
            setAboutForm(data.about || {});
        }
    }, [data]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await fetch('/api/login.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginData)
            });
            const result = await response.json();
            if (result.success) {
                setIsLoggedIn(true);
                localStorage.setItem('adminToken', 'true');
                toast.success('Giriş başarılı');
            } else {
                toast.error('Giriş başarısız: ' + result.message);
            }
        } catch (error) {
            toast.error('Bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        localStorage.removeItem('adminToken');
        toast.info('Oturum kapatıldı');
    };

    const handleUpdate = async (table, fields, id = 1, action = 'update') => {
        setLoading(true);
        try {
            const response = await fetch('/api/update_data.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ table, fields, id, action })
            });
            const result = await response.json();
            if (result.success) {
                toast.success('İşlem başarılı');
                refreshData();
                setIsDialogOpen(false);
                setEditingItem(null);
            } else {
                toast.error('Hata: ' + result.error);
            }
        } catch (error) {
            console.error('Update error:', error);
            toast.error('Bir hata oluştu: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e, callback) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        setLoading(true);
        try {
            const response = await fetch('/api/upload.php', {
                method: 'POST',
                body: formData
            });
            const result = await response.json();
            if (result.url) {
                callback(result.url);
                toast.success('Dosya yüklendi');
            } else {
                toast.error('Yükleme hatası');
            }
        } catch (error) {
            toast.error('Dosya yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const ImageUploadField = ({ label, value, onChange }) => (
        <div className="space-y-2">
            <Label>{label}</Label>
            <div className="flex flex-col space-y-3">
                {value && (
                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                        <img src={value} alt="Preview" className="w-full h-full object-cover" />
                        <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 h-8 w-8"
                            onClick={() => onChange('')}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                )}
                <div className="flex items-center space-x-2">
                    <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="Resim URL veya yükle..." className="flex-1" />
                    <Button variant="outline" className="relative cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Yükle
                        <input
                            type="file"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) => handleFileUpload(e, onChange)}
                        />
                    </Button>
                </div>
            </div>
        </div>
    );

    if (!isLoggedIn) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4">
                <Card className="w-full max-w-md border-slate-800 bg-slate-900 text-white">
                    <CardHeader className="space-y-1 text-center">
                        <div className="flex justify-center mb-4">
                            <div className="p-3 bg-amber-600 rounded-2xl">
                                <LayoutDashboard className="w-8 h-8" />
                            </div>
                        </div>
                        <CardTitle className="text-2xl font-bold">Yönetim Paneli</CardTitle>
                        <CardDescription className="text-slate-400">
                            Devam etmek için giriş yapın
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="username">Kullanıcı Adı</Label>
                                <Input
                                    id="username"
                                    className="bg-slate-800 border-slate-700 text-white"
                                    value={loginData.username}
                                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Şifre</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    className="bg-slate-800 border-slate-700 text-white"
                                    value={loginData.password}
                                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                    required
                                />
                            </div>
                            <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading}>
                                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-50 overflow-hidden">
            {/* Sidebar */}
            <aside className="w-72 bg-slate-950 text-white flex flex-col h-full flex-shrink-0">
                <div className="p-8 border-b border-slate-800">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center">
                            <LayoutDashboard className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="font-black text-lg leading-tight">BAŞKENT</h2>
                            <p className="text-[10px] text-amber-500 font-bold tracking-widest uppercase">Admin Panel</p>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    {[
                        { id: 'settings', label: 'Genel Ayarlar', icon: Settings },
                        { id: 'sliders', label: 'Slider Yönetimi', icon: ImageIcon },
                        { id: 'about', label: 'Hakkımızda', icon: Info },
                        { id: 'services', label: 'Hizmetler', icon: Briefcase },
                        { id: 'projects', label: 'Projeler', icon: Briefcase },
                        { id: 'gallery', label: 'Galeri', icon: ImageIcon },
                        { id: 'messages', label: 'Mesajlar', icon: MessageSquare },
                        { id: 'faqs', label: 'S.S.S', icon: MessageSquare },
                    ].map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${activeTab === item.id ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/20' : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                }`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-800">
                    <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800" onClick={handleLogout}>
                        <LogOut className="w-5 h-5 mr-3" />
                        Çıkış Yap
                    </Button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-8 overflow-y-auto">
                <div className="max-w-6xl mx-auto">
                    <header className="mb-8 flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900">
                                {activeTab === 'settings' && 'Genel Ayarlar'}
                                {activeTab === 'sliders' && 'Slider Yönetimi'}
                                {activeTab === 'about' && 'Hakkımızda Bölümü'}
                                {activeTab === 'services' && 'Hizmetler Yönetimi'}
                                {activeTab === 'projects' && 'Projeler Yönetimi'}
                                {activeTab === 'gallery' && 'Galeri Yönetimi'}
                                {activeTab === 'messages' && 'Gelen Mesajlar'}
                                {activeTab === 'faqs' && 'Sıkça Sorulan Sorular'}
                            </h1>
                            <p className="text-slate-500 mt-1">Sitenizin içeriğini buradan yönetebilirsiniz.</p>
                        </div>
                        <div className="flex space-x-3">
                            <Button variant="outline" onClick={() => window.open('/', '_blank')}>
                                Siteyi Görüntüle
                            </Button>
                            {(activeTab === 'sliders' || activeTab === 'services' || activeTab === 'projects' || activeTab === 'gallery' || activeTab === 'faqs') && (
                                <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => { setEditingItem({ is_active: 1 }); setIsDialogOpen(true); }}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Yeni Ekle
                                </Button>
                            )}
                        </div>
                    </header>

                    {/* Tab Contents */}
                    <div className="space-y-6">
                        {activeTab === 'settings' && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Site Bilgileri</CardTitle>
                                    <CardDescription>Header, footer ve iletişim bilgileri</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label>Site Başlığı</Label>
                                            <Input value={settingsForm.site_title || ''} onChange={(e) => setSettingsForm({ ...settingsForm, site_title: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Telefon</Label>
                                            <Input value={settingsForm.phone || ''} onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>E-posta</Label>
                                            <Input value={settingsForm.email || ''} onChange={(e) => setSettingsForm({ ...settingsForm, email: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>WhatsApp (905...) </Label>
                                            <Input value={settingsForm.whatsapp || ''} onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp: e.target.value })} />
                                        </div>
                                        <div className="space-y-2 md:col-span-2">
                                            <Label>Adres</Label>
                                            <Textarea value={settingsForm.address || ''} onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })} />
                                        </div>
                                        <div className="space-y-2 md:col-span-2">
                                            <Label>Site Açıklaması (SEO)</Label>
                                            <Textarea value={settingsForm.site_description || ''} onChange={(e) => setSettingsForm({ ...settingsForm, site_description: e.target.value })} />
                                        </div>
                                        <ImageUploadField
                                            label="Beyaz Logo"
                                            value={settingsForm.white_logo_url}
                                            onChange={(url) => setSettingsForm({ ...settingsForm, white_logo_url: url })}
                                        />
                                        <ImageUploadField
                                            label="Koyu Logo"
                                            value={settingsForm.logo_url}
                                            onChange={(url) => setSettingsForm({ ...settingsForm, logo_url: url })}
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => handleUpdate('settings', settingsForm)} disabled={loading}>
                                            <Save className="w-4 h-4 mr-2" />
                                            Değişiklikleri Kaydet
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {activeTab === 'settings' && (
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle>SEO & Analitik</CardTitle>
                                    <CardDescription>Google Analytics ve Search Console kodları</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="space-y-2">
                                        <Label>Google Analytics Kodu (G-XXXXX veya Script)</Label>
                                        <Textarea
                                            placeholder="<!-- Global site tag (gtag.js) - Google Analytics --> ..."
                                            value={settingsForm.google_analytics || ''}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, google_analytics: e.target.value })}
                                            className="font-mono text-xs h-32"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Google Search Console Doğrulama Kodu (Meta Tag)</Label>
                                        <Input
                                            placeholder='<meta name="google-site-verification" content="..." />'
                                            value={settingsForm.google_search_console || ''}
                                            onChange={(e) => setSettingsForm({ ...settingsForm, google_search_console: e.target.value })}
                                            className="font-mono text-xs"
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => handleUpdate('settings', settingsForm)} disabled={loading}>
                                            <Save className="w-4 h-4 mr-2" />
                                            SEO Ayarlarını Kaydet
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {activeTab === 'settings' && (
                            <Card className="mt-6">
                                <CardHeader>
                                    <CardTitle>Güvenlik</CardTitle>
                                    <CardDescription>Admin şifresini değiştir</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2 max-w-sm">
                                        <Label>Yeni Şifre</Label>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Yeni şifrenizi girin..."
                                        />
                                    </div>
                                    <Button className="bg-slate-900 hover:bg-slate-800" onClick={handlePasswordChange} disabled={loading}>
                                        Şifreyi Güncelle
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        {activeTab === 'about' && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Hakkımızda İçeriği</CardTitle>
                                    <CardDescription>Ana sayfadaki hakkımızda bölümü</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Rozet (Badge)</Label>
                                            <Input value={aboutForm.badge || ''} onChange={(e) => setAboutForm({ ...aboutForm, badge: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Başlık</Label>
                                            <Input value={aboutForm.title || ''} onChange={(e) => setAboutForm({ ...aboutForm, title: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>İçerik Paragraf 1</Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={aboutForm.content1 || ''}
                                                onChange={(content) => setAboutForm({ ...aboutForm, content1: content })}
                                                modules={quillModules}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>İçerik Paragraf 2</Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={aboutForm.content2 || ''}
                                                onChange={(content) => setAboutForm({ ...aboutForm, content2: content })}
                                                modules={quillModules}
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <ImageUploadField
                                                label="Resim 1"
                                                value={aboutForm.image1_url}
                                                onChange={(url) => setAboutForm({ ...aboutForm, image1_url: url })}
                                            />
                                            <ImageUploadField
                                                label="Resim 2"
                                                value={aboutForm.image2_url}
                                                onChange={(url) => setAboutForm({ ...aboutForm, image2_url: url })}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => handleUpdate('about_section', aboutForm)} disabled={loading}>
                                            <Save className="w-4 h-4 mr-2" />
                                            Değişiklikleri Kaydet
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {activeTab === 'messages' && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Gelen Mesajlar</CardTitle>
                                    <CardDescription>İletişim formu üzerinden gelen talepler</CardDescription>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Gönderen</TableHead>
                                                <TableHead>Konu / Mesaj</TableHead>
                                                <TableHead>Tarih</TableHead>
                                                <TableHead className="text-right">İşlemler</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data?.messages?.map((msg) => (
                                                <TableRow key={msg.id} className={msg.is_read ? 'opacity-60' : 'bg-amber-50/30'}>
                                                    <TableCell>
                                                        <div className="font-bold">{msg.full_name}</div>
                                                        <div className="text-sm text-slate-500">{msg.phone}</div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="font-medium">{msg.subject || 'Konu Yok'}</div>
                                                        <div className="text-sm text-slate-600 max-w-md truncate">{msg.message}</div>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-500">
                                                        {new Date(msg.created_at).toLocaleString('tr-TR')}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end space-x-2">
                                                            {!msg.is_read && (
                                                                <Button variant="ghost" size="icon" onClick={() => handleUpdate('messages', { is_read: 1 }, msg.id)}>
                                                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                                </Button>
                                                            )}
                                                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => { if (window.confirm('Emin misiniz?')) handleUpdate('messages', {}, msg.id, 'delete') }}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!data?.messages || data.messages.length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-12 text-slate-500">
                                                        Henüz mesaj bulunmuyor.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}

                        {(activeTab === 'sliders' || activeTab === 'services' || activeTab === 'projects' || activeTab === 'gallery' || activeTab === 'faqs') && (
                            <Card>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Başlık / İçerik</TableHead>
                                                <TableHead>Durum</TableHead>
                                                <TableHead className="text-right">İşlemler</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data && data[activeTab]?.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <div className="font-medium">{item.title || item.question}</div>
                                                        <div className="text-sm text-slate-500 truncate max-w-xs" dangerouslySetInnerHTML={{ __html: item.subtitle || item.description || item.answer || '' }}></div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`px-2 py-1 rounded-full text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                                                            {item.is_active ? 'Aktif' : 'Pasif'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end space-x-2">
                                                            <Button variant="ghost" size="icon" onClick={() => { setEditingItem(item); setIsDialogOpen(true); }}>
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => { if (window.confirm('Emin misiniz?')) handleUpdate(activeTab, {}, item.id, 'delete') }}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(!data || !data[activeTab] || data[activeTab].length === 0) && (
                                                <TableRow>
                                                    <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                                                        Kayıt bulunamadı.
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </main>

            {/* Dialog for Add/Edit */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingItem?.id ? 'Düzenle' : 'Yeni Ekle'}</DialogTitle>
                        <DialogDescription>Gerekli bilgileri doldurun.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {activeTab === 'sliders' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Rozet</Label>
                                    <Input value={editingItem?.badge || ''} onChange={(e) => setEditingItem({ ...editingItem, badge: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Başlık</Label>
                                    <Input value={editingItem?.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Alt Başlık</Label>
                                    <Textarea value={editingItem?.subtitle || ''} onChange={(e) => setEditingItem({ ...editingItem, subtitle: e.target.value })} />
                                </div>
                                <ImageUploadField
                                    label="Slider Resmi"
                                    value={editingItem?.image_url}
                                    onChange={(url) => setEditingItem({ ...editingItem, image_url: url })}
                                />
                            </>
                        )}
                        {activeTab === 'services' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Hizmet Adı</Label>
                                    <Input value={editingItem?.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Açıklama</Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={editingItem?.description || ''}
                                        onChange={(content) => setEditingItem({ ...editingItem, description: content })}
                                        modules={quillModules}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>İkon (Lucide Icon Name)</Label>
                                    <Input value={editingItem?.icon_name || 'Zap'} onChange={(e) => setEditingItem({ ...editingItem, icon_name: e.target.value })} />
                                </div>
                            </>
                        )}
                        {activeTab === 'projects' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Proje Adı</Label>
                                    <Input value={editingItem?.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Kategori</Label>
                                    <Input value={editingItem?.category || ''} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Açıklama</Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={editingItem?.description || ''}
                                        onChange={(content) => setEditingItem({ ...editingItem, description: content })}
                                        modules={quillModules}
                                    />
                                </div>

                            </>
                        )}
                        {activeTab === 'gallery' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Resim Başlığı</Label>
                                    <Input value={editingItem?.title || ''} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Kategori</Label>
                                    <Input value={editingItem?.category || ''} onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })} />
                                </div>
                                <ImageUploadField
                                    label="Galeri Resmi"
                                    value={editingItem?.image_url}
                                    onChange={(url) => setEditingItem({ ...editingItem, image_url: url })}
                                />
                            </>
                        )}
                        {activeTab === 'faqs' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Soru</Label>
                                    <Input value={editingItem?.question || ''} onChange={(e) => setEditingItem({ ...editingItem, question: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Cevap</Label>
                                    <ReactQuill
                                        theme="snow"
                                        value={editingItem?.answer || ''}
                                        onChange={(content) => setEditingItem({ ...editingItem, answer: content })}
                                        modules={quillModules}
                                    />
                                </div>
                            </>
                        )}
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={editingItem?.is_active === 1}
                                onChange={(e) => setEditingItem({ ...editingItem, is_active: e.target.checked ? 1 : 0 })}
                            />
                            <Label htmlFor="is_active">Aktif mi?</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
                        <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => {
                            handleUpdate(activeTab, editingItem, editingItem?.id, editingItem?.id ? 'update' : 'insert');
                        }}>Kaydet</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Admin;
